export interface IKeyboardFocusTarget {
  readonly x: number;
  readonly y: number;
  readonly activate: () => void;
  readonly setFocused: (focused: boolean) => void;
}

export const KeyboardNavigationResult = {
  Ignored: 'ignored',
  Moved: 'moved',
  Activated: 'activated',
} as const;
export type TKeyboardNavigationResult =
  (typeof KeyboardNavigationResult)[keyof typeof KeyboardNavigationResult];

const KeyboardAxis = {
  X: 'x',
  Y: 'y',
} as const;
type TKeyboardAxis = (typeof KeyboardAxis)[keyof typeof KeyboardAxis];

const KeyboardDirectionSign = {
  Backward: -1,
  Forward: 1,
} as const;
type TKeyboardDirectionSign =
  (typeof KeyboardDirectionSign)[keyof typeof KeyboardDirectionSign];

const CONFIRM_KEYS: readonly string[] = ['Enter', ' ', 'Spacebar'];

/** 依畫面座標尋找方向上最近控制項的通用鍵盤／電視焦點控制器。 */
export class CSpatialKeyboardNavigator {
  private targets: readonly IKeyboardFocusTarget[] = [];
  private focusIndex = -1;

  setTargets(targets: readonly IKeyboardFocusTarget[], preferredIndex = 0): void {
    this.clearFocus();
    this.targets = targets;
    if (targets.length > 0) this.focus(Math.min(Math.max(preferredIndex, 0), targets.length - 1));
  }

  clear(): void {
    this.clearFocus();
    this.targets = [];
  }

  handleKey(key: string): TKeyboardNavigationResult {
    if (this.targets.length === 0) return KeyboardNavigationResult.Ignored;
    if (CONFIRM_KEYS.includes(key)) {
      this.targets[this.focusIndex]?.activate();
      return KeyboardNavigationResult.Activated;
    }

    const direction = this.directionForKey(key);
    if (!direction) return KeyboardNavigationResult.Ignored;
    const current = this.targets[this.focusIndex];
    if (!current) return KeyboardNavigationResult.Ignored;

    let bestIndex = -1;
    let bestScore = Number.POSITIVE_INFINITY;
    this.targets.forEach((candidate, index) => {
      if (index === this.focusIndex) return;
      const dx = candidate.x - current.x;
      const dy = candidate.y - current.y;
      const primary = direction.axis === 'x' ? dx * direction.sign : dy * direction.sign;
      if (primary <= 0) return;
      const secondary = direction.axis === 'x' ? Math.abs(dy) : Math.abs(dx);
      const score = primary + secondary * 2;
      if (score < bestScore) {
        bestScore = score;
        bestIndex = index;
      }
    });
    if (bestIndex < 0) return KeyboardNavigationResult.Ignored;
    this.focus(bestIndex);
    return KeyboardNavigationResult.Moved;
  }

  focus(index: number): void {
    if (!this.targets[index]) return;
    this.clearFocus();
    this.focusIndex = index;
    this.targets[index]!.setFocused(true);
  }

  getFocusIndex(): number {
    return this.focusIndex;
  }

  private clearFocus(): void {
    this.targets[this.focusIndex]?.setFocused(false);
    this.focusIndex = -1;
  }

  private directionForKey(
    key: string
  ): { readonly axis: TKeyboardAxis; readonly sign: TKeyboardDirectionSign } | null {
    if (key === 'ArrowLeft')
      return { axis: KeyboardAxis.X, sign: KeyboardDirectionSign.Backward };
    if (key === 'ArrowRight')
      return { axis: KeyboardAxis.X, sign: KeyboardDirectionSign.Forward };
    if (key === 'ArrowUp')
      return { axis: KeyboardAxis.Y, sign: KeyboardDirectionSign.Backward };
    if (key === 'ArrowDown')
      return { axis: KeyboardAxis.Y, sign: KeyboardDirectionSign.Forward };
    return null;
  }
}
